<template>
  <collapsible-card
    :class="{ 'user-items-card': isExpanded }"
    :expanded="isExpanded"
    @expand="isExpanded = $event"
    v-if="isDataAvailable || isDataNull"
  >
    <template slot="heading">
      <heading-tabs :tabs="tabs" :selected="selectedTab" @select="onTabChange" />
    </template>
    <template slot="content">
      <div class="section-container">
        <div class="skeleton-loader ml-4" v-if="goalsLoadingState === LoadingState.LOADING">
          <goal-skeleton class="pl-0" />
          <goal-skeleton class="pl-0" />
          <goal-skeleton class="pl-0" />
        </div>
        <div
          class="no-result text-secondary"
          v-else-if="goalsLoadingState === LoadingState.ERRORED"
        >
          Error while fetching goals
        </div>
        <template v-else-if="goalsToDisplay && goalsToDisplay.length > 0">
          <goal-viewer
            v-for="goal in goalsToDisplay"
            :key="goal.id"
            :goal-id="goal.id"
            :show-type="true"
            :show-child-goals="false"
            :show-goal-archival-info="false"
            :selected-timeline="selectedTimeline"
            :show-gripper="false"
            :selectable="false"
            :source="source"
            @archive-goal="$emit('archived')"
          ></goal-viewer>
        </template>
        <div class="no-result text-secondary" v-no-capture v-else>
          {{ isLoggedInUser ? "you don't" : `${user.firstName || user.email} doesn't` }} have any
          goals yet
        </div>
      </div>
      <div @seen="isInitiativesSectionSeen = true" v-observe v-if="!isInitiativesSectionSeen"></div>
      <user-initiatives
        class="mb-3"
        v-if="isInitiativesSectionSeen && (!initiatives || initiatives.length > 0)"
        :org-id="orgId"
        :user-id="userId"
        :fetcher="params => initiativesFetcher(ENTITY_TYPE.INITIATIVE, params)"
        :entity-type="ENTITY_TYPE.INITIATIVE"
      />
      <user-initiatives
        class="mb-3"
        v-if="isInitiativesSectionSeen && (!tasks || tasks.length > 0) && isTasksFeatureEnabled"
        :org-id="orgId"
        :user-id="userId"
        :fetcher="params => initiativesFetcher(ENTITY_TYPE.TASK, params)"
        :entity-type="ENTITY_TYPE.TASK"
      />
    </template>
  </collapsible-card>
</template>

<script>
import CollapsibleCard from '@/components/CollapsibleCard'
import GoalViewer from '@/components/GoalViewer'
import GoalSkeleton from '@/components/skeletons/GoalSkeleton'
import HeadingTabs from '@/components/user-page/HeadingTabs'
import UserInitiatives from '@/components/user-page/UserInitiatives'
import isUserNonPrimaryOwner from '@/components/user-page/utils/isUserNonPrimaryOwner'
import isUserPrimaryOwner from '@/components/user-page/utils/isUserPrimaryOwner'
import { SOURCE } from '@/constants/analytics'
import { LoadingState } from '@/constants/common'
import { ENTITY_TYPE } from '@/constants/entities'
import { featureFlags } from '@/constants/featureFlags'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import * as R from 'ramda'
import observe from '@/directives/observe'

const TABS = {
  OWNED: 'owned',
  CO_OWNED: 'co-owned'
}

/**
 * This component shows the user's Goals, Initiatives, Tasks in the selected org
 *
 * Props:
 *  - goals
 *  - userId
 *  - selectedTimeline
 *  - orgId
 *  - userInitiativesFetcher
 *    => This prop needs to be a function that returns a promise which resolves to give list of initiatives
 */
export default {
  name: 'UserAssignedItemsCard',
  props: ['userId', 'selectedTimeline', 'orgId', 'userGoalsFetcher', 'userInitiativesFetcher'],
  mixins: [FeaturesMixin],
  components: {
    CollapsibleCard,
    GoalViewer,
    HeadingTabs,
    UserInitiatives,
    GoalSkeleton
  },
  directives: {
    observe
  },
  data() {
    return {
      selectedTab: null,
      isExpanded: true,
      goals: null,
      initiatives: null,
      tasks: null,
      ENTITY_TYPE,
      isInitiativesSectionSeen: false,
      goalsLoadingState: LoadingState.LOADING,
      LoadingState,
      source: SOURCE.YOUR_GOALS_SECTION,
      isTasksFeatureEnabled: this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_TASKS)
    }
  },
  computed: {
    tabs() {
      return [
        {
          id: TABS.OWNED,
          label: this.isLoggedInUser ? 'All Goals you own' : `All Goals ${this.user.firstName} owns`
        },
        { id: TABS.CO_OWNED, label: this.isLoggedInUser ? 'co-own' : 'co-owns' }
      ]
    },
    user() {
      return this.$store.getters.userById(this.ownerId) || {}
    },
    ownerId() {
      const userId = this.userId || this.$store.getters.user.id
      return Number(userId)
    },
    isLoggedInUser() {
      const loggedInUser = this.$store.getters.user
      return this.ownerId === loggedInUser.id
    },
    ownedGoals() {
      if (this.goals) {
        return this.goals.filter(
          goal =>
            this.isUserPrimaryOwner(goal.ownerships) ||
            goal.keyResults.some(kr => !kr.isArchived && this.isUserPrimaryOwner(kr.ownerships))
        )
      }
      return []
    },
    coOwnedGoals() {
      if (this.goals) {
        const coOwnedGoals = this.goals.filter(
          goal =>
            this.isUserNonPrimaryOwner(goal.ownerships) ||
            goal.keyResults.some(kr => !kr.isArchived && this.isUserNonPrimaryOwner(kr.ownerships))
        )
        return R.differenceWith((a, b) => a.id === b.id, coOwnedGoals, this.ownedGoals)
      }
      return []
    },
    goalsToDisplay() {
      return this.selectedTab === TABS.OWNED ? this.ownedGoals : this.coOwnedGoals
    },
    entitiesData() {
      const items = [this.goals, this.initiatives]
      if (this.isTasksFeatureEnabled) {
        items.push(this.tasks)
      }
      return items
    },
    isDataNull() {
      return this.entitiesData.some(R.isNil)
    },
    isDataAvailable() {
      return this.entitiesData.some(item => item?.length > 0)
    }
  },
  methods: {
    onTabChange(selectedTab) {
      this.isExpanded = true
      this.selectedTab = selectedTab
    },
    isUserPrimaryOwner(ownerships) {
      return isUserPrimaryOwner(ownerships, this.ownerId)
    },
    isUserNonPrimaryOwner(ownerships) {
      return isUserNonPrimaryOwner(ownerships, this.ownerId)
    },
    async initiativesFetcher(entityType, params) {
      let hasParent = false
      if (entityType === ENTITY_TYPE.TASK) {
        hasParent = true
      }
      params = { ...params, hasParent }
      const response = await this.userInitiativesFetcher(params)
      if (entityType === ENTITY_TYPE.INITIATIVE) {
        this.initiatives = response.results
      } else {
        this.tasks = response.results
      }
      return response.results
    },
    async refreshGoals() {
      try {
        this.goalsLoadingState = LoadingState.LOADING
        this.goals = await this.userGoalsFetcher()
        this.goalsLoadingState = LoadingState.LOADED
      } catch (e) {
        this.goalsLoadingState = LoadingState.ERRORED
        EventBus.$emit('ADD_TOAST', {
          message: 'Errored while fetching goals',
          type: TOAST_TYPE.ERROR
        })
      }
    }
  },
  mounted() {
    this.refreshGoals()
  },
  watch: {
    goals(newGoals, oldGoals) {
      if (!oldGoals && newGoals) {
        if (this.ownedGoals.length === 0 && this.coOwnedGoals.length > 0) {
          this.selectedTab = TABS.CO_OWNED
        } else {
          this.selectedTab = TABS.OWNED
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .goal-title-container {
  padding: 0 8px;
}

.user-items-card {
  min-height: 300px;
}

.no-result {
  padding-left: 16px;
}
</style>
